import React from 'react';
import useLanguage from '../hooks/useLanguage';
import { formatShortDateTime } from '../utils/dateFormatter';
import '../styles/GuestCheckoutLine.css';

/**
 * «غادر الضيف ٠٩:١٥ — تأكيد الفريق» — the guest-checkout record, on a cleaning task.
 * tasks/manual-guest-checkout-event.md Part 2 (D27, D42, D43).
 *
 * ONE component, FOUR surfaces: the ops list table, the ops card, the ops edit modal and the crew's
 * `/my-tasks` card. It exists as a shared piece rather than four copies because the wording is the
 * feature — D21's evidence-honesty and D23's never-instruct only hold if there is one place to hold
 * them. Four copies would drift on the first edit.
 *
 * ── READ-ONLY, ALWAYS ─────────────────────────────────────────────────────────────────────────
 * There is no control here and there must never be one. Recording a checkout is core-staff work on
 * the departures card and the booking page (D7); this only DISPLAYS what the booking already says
 * (D27 — the task never owns a copy).
 *
 * ── It informs; it never instructs (D23) ──────────────────────────────────────────────────────
 * «غادر الضيف», never «ابدأ التنظيف». A wrong press then costs a moment's confusion rather than a
 * cleaner at an occupied door — which is the entire reason this feature is allowed to exist without
 * an approval step.
 *
 * ── The source is not decoration (D21, D42) ───────────────────────────────────────────────────
 * A human confirmation and a keypad event are different claims, so they get different words. Ops
 * phoning the guest is strong evidence. A keypad proves a door opened — the guest may be carrying a
 * bag to the car. The line therefore always names WHERE the claim came from, and a device source
 * says «مغادرة محتملة», not «غادر الضيف». This is also what keeps the record honest for the future
 * AI layer, which cites these surfaces rather than re-deriving them (OPS_ARCHITECTURE §1).
 *
 * @param {{observedAt: string, source: string, recordedByName: string|null}} checkout
 *        The backend's derived `task.guestCheckout` — an ISO instant plus a machine source, never
 *        pre-formatted text (I18N_STRATEGY: the API is language-neutral).
 * @param {boolean} compact  Drops the recorder's name. For the two space-constrained card surfaces,
 *                           where the fact matters and the attribution is one tap away.
 */
const GuestCheckoutLine = ({ checkout, compact = false, className = '' }) => {
  const { t, isArabic, language } = useLanguage();

  if (!checkout?.observedAt) return null;

  // ⚠️ KEYED ON THE SOURCE, not a manual/not-manual boolean. That binary was correct while the only
  // alternative was a lock; the moment `'guest'` joined the enum it labelled a guest's own press
  // «القفل الذكي» — a smart lock that was never involved. Three claims, three wordings, and an
  // unknown source falls back to the weakest rather than borrowing the strongest.
  const HEADLINE = {
    manual: 'tasks.guestCheckout.recorded',       // ops phoned or saw them go
    guest: 'tasks.guestCheckout.guestReported',   // the guest pressed the plaque themselves
    device: 'tasks.guestCheckout.possible'        // a door opened; nobody said anyone left
  };
  const SOURCE = {
    manual: 'tasks.guestCheckout.sourceManual',
    guest: 'tasks.guestCheckout.sourceGuest',
    device: 'tasks.guestCheckout.sourceDevice'
  };

  const kind = HEADLINE[checkout.source] ? checkout.source : 'device';
  const headline = t(HEADLINE[kind]);
  const sourceLabel = t(SOURCE[kind]);

  // `observedAt` is an INSTANT (TEMPORAL_FIELD_CATALOGUE) — the business timezone applies, and this
  // formatter is the only thing allowed to apply it. Never a local formatter, never an inline
  // 'Asia/Riyadh'.
  const when = formatShortDateTime(checkout.observedAt, language);

  return (
    // `textAlign` explicitly, not left to `dir`: two of the four host surfaces put this line in a
    // block whose siblings carry Bootstrap's `text-end`, which under the app's RTL Bootstrap build
    // resolves to `text-align: left`. Inheriting would leave the line on the opposite side from
    // everything it sits with.
    <div
      className={`guest-checkout-line ${className}`.trim()}
      dir={isArabic ? 'rtl' : 'ltr'}
      style={{ textAlign: isArabic ? 'right' : 'left' }}
    >
      <span className="guest-checkout-line__fact">
        <i className="bi bi-box-arrow-right" aria-hidden="true" />
        {/* Logical property, not Bootstrap's `me-1`: the app loads the RTL build of Bootstrap
            globally, so `me-*` resolves to the wrong side for an English-locale cleaner. */}
        <span style={{ marginInlineStart: '0.35rem' }}>
          {headline} {when}
        </span>
      </span>
      <span className="guest-checkout-line__source">
        {' — '}{sourceLabel}
        {!compact && checkout.recordedByName
          ? ` · ${t('tasks.guestCheckout.recordedBy')} ${checkout.recordedByName}`
          : ''}
      </span>
    </div>
  );
};

export default GuestCheckoutLine;
